import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTramaClient } from '../context.js';

interface ComponentManifestEntry {
  name: string;
  version: string;
  bundleUrl: string;
  bundleHash: string;
}

interface ManifestResponse {
  components: ComponentManifestEntry[];
}

declare global {
  var TramaComponents: Record<string, React.ComponentType<unknown>> | undefined;
}

const loaded = new Map<string, Promise<unknown>>();

function loadBundleOnce(entry: ComponentManifestEntry): Promise<unknown> {
  const cacheKey = `${entry.name}@${entry.version}#${entry.bundleHash}`;
  const existing = loaded.get(cacheKey);
  if (existing) return existing;

  const promise = new Promise<unknown>((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('useAgencyComponent: not in a browser environment'));
      return;
    }

    const existingComponent = globalThis.TramaComponents?.[entry.name];
    if (existingComponent) {
      resolve(existingComponent);
      return;
    }

    const script = document.createElement('script');
    script.src = entry.bundleUrl;
    script.async = true;
    script.crossOrigin = 'anonymous';

    script.onload = () => {
      const component = globalThis.TramaComponents?.[entry.name];
      if (component) resolve(component);
      else reject(new Error(`Bundle loaded but did not register component: ${entry.name}`));
    };

    script.onerror = () => reject(new Error(`Failed to load component bundle: ${entry.bundleUrl}`));
    document.head.appendChild(script);
  });

  loaded.set(cacheKey, promise);
  return promise;
}

export interface UseAgencyComponentReturn<P = Record<string, unknown>> {
  Component: React.ComponentType<P> | null;
  isLoading: boolean;
  error: Error | null;
}

export function useAgencyComponent<P = Record<string, unknown>>(name: string): UseAgencyComponentReturn<P> {
  const client = useTramaClient();

  const { data: manifest, error: manifestError } = useQuery({
    queryKey: ['component-manifest'],
    queryFn: () => client.request<ManifestResponse>('/api/v1/components/manifest'),
    staleTime: 60 * 1000,
  });

  const [Component, setComponent] = useState<React.ComponentType<P> | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!manifest) return;
    const entry = manifest.components.find((c) => c.name === name);
    if (!entry) {
      setError(new Error(`Component "${name}" is not deployed to this project`));
      return;
    }

    let cancelled = false;
    loadBundleOnce(entry)
      .then((c) => {
        if (!cancelled) setComponent(() => c as React.ComponentType<P>);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err);
      });

    return () => {
      cancelled = true;
    };
  }, [manifest, name]);

  return {
    Component,
    isLoading: !manifest && !manifestError,
    error: error ?? (manifestError as Error | null),
  };
}
